import React, { useEffect, useState } from 'react';

import InputLabelWrapper from 'components/basic/input-label-wrapper/InputLabelWrapper';
import { Attribute, ChildrenConfiguration, DatasetMode, InterruptedService, MaintenanceEmail, MdmProjectDto, MdmProjectInputInput, MeasurementTypeDef, useGetProjectQuery, useUpdateProjectMutation } from 'gql/generated/types';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { useParams } from 'react-router-dom';
import { T } from 'services/lang/LanguageService';
import AdditionalProjectSettings from '../components/Settings/AdditionalProjectSettings';
import DeviceRoles from '../components/Settings/DeviceRoles';
import { apolloErrorToaster, errorToaster, successToaster } from '../utils/utils';
import MaintenanceConfiguration from '../components/Maintenance/MaintenanceConfiguration';
import NodesAttributesConfiguration from '../components/Settings/NodesAttributesConfiguration';
import DeviceConfiguration from '../components/Settings/DeviceConfiguration';

const Settings = () => {
  const [projectConfig, setProjectConfig] = useState<MdmProjectDto | null>(null);
  const [objAttributes, setObjAttributes] = useState<Attribute[]>([]);
  const [childrenAttributes, setChildrenAttributes] = useState<ChildrenConfiguration[]>([]);
  const [deviceAttributes, setDeviceAttributes] = useState<Attribute[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<MeasurementTypeDef[]>([]);
  const [emails, setEmails] = useState<MaintenanceEmail[]>([]);
  const [interruptedServices, setInterruptedServices] = useState<InterruptedService[]>([]);

  const { projectId } = useParams();

  const { data } = useGetProjectQuery({ variables: { id: projectId! }, skip: !projectId });

  const [updateProjectMutation] = useUpdateProjectMutation({
    onCompleted: () => successToaster(T("alert.project-updated-successfully")),
    onError: apolloErrorToaster,
  });

  useEffect(() => {
    if (data) {
      const {filterInfo, ...rest } = data.mdmProjectById;
      setProjectConfig(rest as MdmProjectDto);
      setObjAttributes((data.mdmProjectById?.configuration?.objectConfiguration?.customFields ?? []) as Attribute[]);
      setChildrenAttributes((data.mdmProjectById?.configuration?.childrenConfiguration ?? []) as ChildrenConfiguration[]);
      setDeviceAttributes((data.mdmProjectById?.configuration?.deviceConfiguration?.customFields ?? []) as Attribute[]);
      setDeviceTypes((data.mdmProjectById?.configuration?.deviceConfiguration?.measurementTypes ?? []) as MeasurementTypeDef[]);
      setEmails((data.mdmProjectById?.configuration?.maintenanceConfiguration.emails ?? []) as MaintenanceEmail[]);
      setInterruptedServices((data.mdmProjectById?.configuration?.maintenanceConfiguration?.interruptedServices ?? []) as InterruptedService[]);
    }
  }, [data]);

  const handleSubmit = () => {
    const attributes = [
      ...objAttributes,
      ...childrenAttributes.map(attr => attr.customFields as unknown as Attribute),
      ...deviceAttributes,
    ];
    const attributeIds = attributes.map(attr => attr.id);
    const duplicateIds = attributeIds.filter((id, index) => attributeIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      errorToaster(T("alert.duplicate-attribute-id", {ids: duplicateIds.join(", ")}));
      return;
    }

    const project: MdmProjectInputInput = {
      ...projectConfig,
      configuration: {
        ...projectConfig?.configuration,
        analyticsConfiguration: {
          ...projectConfig!.configuration!.analyticsConfiguration,
        },
        deviceConfiguration: {
          customFields: deviceAttributes,
          measurementTypes: deviceTypes
        },
        objectConfiguration: {
          ...projectConfig!.configuration!.objectConfiguration,
          customFields: objAttributes,
        },
        childrenConfiguration: childrenAttributes,
        maintenanceConfiguration: {
          emails,
          interruptedServices
        }
      },
    };
    updateProjectMutation({ variables: { project }, refetchQueries: ["GetProject"] });
  }

  return (
    <div className="grid p-fluid m-3 w-full mx-auto">
      <InputLabelWrapper labelName={T("word.project")} col={4}>
        <InputText
          value={projectConfig?.title ?? ""}
          type="text"
          placeholder={T("word.project-name")}
          onChange={e =>
            setProjectConfig(prev => ({ ...(prev as MdmProjectDto), title: e.target.value }))
          }
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("load-conf.timezone-id")} col={3}>
        <Dropdown
          value={projectConfig?.timezone}
          options={Intl.supportedValuesOf("timeZone")}
          filter
          placeholder={T("word.select-one")}
          onChange={e =>
            setProjectConfig(prev => ({ ...(prev as MdmProjectDto), timezone: e.target.value }))
          }
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("mdm.dataset-mode")} col={3}>
        <Dropdown
          value={projectConfig?.datasetMode}
          placeholder={T("word.select-one")}
          options={Object.entries(DatasetMode).map(([key, value]) => ({ label: key, value }))}
          onChange={e =>
            setProjectConfig(prev => ({ ...(prev as MdmProjectDto), datasetMode: e.target.value }))
          }
        />
      </InputLabelWrapper>
      <div className="flex w-full justify-content-end gap-3">
        <Button
          type="submit"
          onClick={handleSubmit}
          label={T("action.update")}
          className="p-button-rounded p-button-primary w-10rem"
        />
      </div>

      <TabView className="col-12">
        <TabPanel header={T("mdm.object-attributes")}>
          <AdditionalProjectSettings attributes={objAttributes} setAttributes={setObjAttributes} />
        </TabPanel>
        <TabPanel header={T("mdm.nodes-attributes")}>
          <NodesAttributesConfiguration
            childrenAttributes={childrenAttributes}
            setChildrenAttributes={setChildrenAttributes}
          />
        </TabPanel>
        <TabPanel header={T("mdm.device-attributes")}>
          <AdditionalProjectSettings
            attributes={deviceAttributes}
            setAttributes={setDeviceAttributes}
          />
        </TabPanel>
        <TabPanel header={T("mdm.device-roles")}>
          <DeviceRoles types={deviceTypes} setTypes={setDeviceTypes} />
        </TabPanel>
        <TabPanel header={T("word.device-config")}>
          <DeviceConfiguration configurations={projectConfig?.deviceConfigs} />
        </TabPanel>
        <TabPanel header={T("mdm.maintenance-config")}>
          <MaintenanceConfiguration
            deviceRoles={deviceTypes}
            emails={emails}
            setEmails={setEmails}
            interruptedServices={interruptedServices}
            setInterruptedServices={setInterruptedServices}
          />
        </TabPanel>
      </TabView>
    </div>
  );
}

export default Settings;
